import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BaseInput from '../ui/BaseInput';
import { Button } from '../ui';
import ErrorBanner from '../ui/ErrorBanner';
import { VerifyEmailFormData, VerifyEmailErrors } from '../../types/auth';
import { API_URLS } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoggedIn, checkSession } = useAuth();
  
  const [formData, setFormData] = useState<VerifyEmailFormData>({
    email: searchParams.get('email') || '',
    code: '',
  });
  
  const [errors, setErrors] = useState<VerifyEmailErrors>({});
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (field: keyof VerifyEmailFormData) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: VerifyEmailErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Verification code is required';
    } else if (!/^\d{6}$/.test(formData.code.trim())) {
      newErrors.code = 'Please enter a valid 6-digit code';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      const response = await fetch(API_URLS.AUTH.VERIFY_EMAIL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          code: formData.code.trim(),
        }),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Update authentication state immediately
        await checkSession();
        
        // Redirect immediately to main page without forcing login
        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath);
        } else {
          navigate('/');
        }
      } else {
        if (response.status === 400 && result.fieldErrors) {
          setErrors(result.fieldErrors);
        } else {
          setErrors({ 
            general: result.message || 'Verification failed. Please check your code and try again.' 
          });
        }
      }
    } catch (error) {
      console.error('Verification error:', error instanceof Error ? error.message : 'Unknown error');
      setErrors({ 
        general: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!formData.email.trim()) {
      setErrors({ email: 'Please enter your email address first' });
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }
    
    setResendLoading(true);
    setErrors({});
    setResendSuccess(false);
    
    try {
      const response = await fetch(API_URLS.AUTH.RESEND_VERIFICATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
        }),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setResendSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        setFormData(prev => ({ ...prev, code: '' })); // Clear the code field
      } else {
        setErrors({ 
          general: result.message || 'Failed to resend verification code. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Resend error:', error instanceof Error ? error.message : 'Unknown error');
      setErrors({ 
        general: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a 6-digit verification code to your email address.
            {formData.email && (
              <span className="block font-medium text-gray-900 mt-1">
                {formData.email}
              </span>
            )}
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {errors.general && (
            <ErrorBanner
              title="Verification Failed"
              message={errors.general}
              type="error"
            />
          )}
          
          {resendSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm text-center">
              ✅ Verification code sent! Please check your email.
            </div>
          )}
          
          <div className="space-y-4">
            <BaseInput
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={errors.email}
              required
              placeholder="your@email.com"
              autoComplete="email"
            />
            
            <BaseInput
              label="6-Digit Verification Code"
              name="code"
              type="text"
              value={formData.code}
              onChange={handleChange('code')}
              error={errors.code}
              required
              placeholder="123456"
              maxLength={6}
              pattern="\d{6}"
              autoComplete="one-time-code"
              helperText="Enter the 6-digit code from your email"
            />
          </div>
          
          <div className="space-y-4">
            <Button
              type="submit"
              loading={loading}
              className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </Button>
            
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendLoading || resendCooldown > 0}
                className={`text-sm font-medium transition-colors duration-200 ${
                  resendLoading || resendCooldown > 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-brand-electric-blue hover:text-brand-dark-blue cursor-pointer'
                }`}
              >
                {resendLoading ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span>Sending...</span>
                  </span>
                ) : resendCooldown > 0 ? (
                  `Resend code in ${resendCooldown}s`
                ) : (
                  'Resend verification code'
                )}
              </button>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Having trouble?{' '}
              <button
                type="button"
                onClick={() => navigate('/auth/signup')}
                className="text-brand-electric-blue hover:text-brand-dark-blue font-medium"
              >
                Back to Registration
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmailPage;